/**
 * Labware classification utility — replaces typeId string matching
 * with structured config lookups.
 *
 * Each function looks up the labware in the active config and checks
 * structured fields (category, deploymentType, deviceRole, adapterType,
 * wellShape specs, and labels). TypeId string patterns are NOT checked.
 */

import type { LabwareType } from "@/automation/system-config/systemConfigParser";

/** Config-like object containing a labware array. Accepts ParsedFullConfig, SystemConfig, etc. */
export type LabwareConfig = { labware: LabwareType[] } | null | undefined;

function findLabware(
  typeId: string,
  config: LabwareConfig,
): LabwareType | undefined {
  if (!config?.labware) return undefined;
  return config.labware.find((lw) => lw.id === typeId);
}

function getSpecValue(
  lw: LabwareType,
  specName: string,
): string | number | undefined {
  return lw.specifications?.find((s) => s.name === specName)?.value;
}

// ---------------------------------------------------------------------------
// Category checks
// ---------------------------------------------------------------------------

/** True if labware is in the "tip" category (any deployment: tray, box, rack). */
export function isTipLabware(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (lw.category === "tip") return true;
  return String(getSpecValue(lw, "wellShape") ?? "").toLowerCase() === "tip";
}

/** True if labware is an 8-channel adapter. */
export function isAdapter8Channel(
  typeId: string,
  config: LabwareConfig,
): boolean {
  return findLabware(typeId, config)?.adapterType === "8channel";
}

/** True if labware is a 12-channel adapter. */
export function isAdapter12Channel(
  typeId: string,
  config: LabwareConfig,
): boolean {
  return findLabware(typeId, config)?.adapterType === "12channel";
}

/** True if labware is any adapter type. */
export function isAdapter(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  return lw?.category === "adapter" || !!lw?.adapterType;
}

// ---------------------------------------------------------------------------
// Tip deployment checks
// ---------------------------------------------------------------------------

/** True if labware is a tip tray (whole-tray pickup by head). */
export function isTipTray(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (lw.deploymentType === "tiptray") return true;
  // Fallback: tipStand starts with "stand" (e.g. "stand105")
  if (lw.tipStand?.startsWith("stand")) return true;
  return false;
}

/** True if labware is a tip box (individual tip pickup via adapter). */
export function isTipBox(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (lw.deploymentType === "tipbox") return true;
  return lw.tipStand === "tipbox";
}

/** True if labware is a tip rack. */
export function isTipRack(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (lw.deploymentType === "tiprack") return true;
  return lw.tipStand === "tiprack";
}

// ---------------------------------------------------------------------------
// Tip-return vessel matching
// ---------------------------------------------------------------------------

/**
 * Nominal tip volume (µL) parsed from a tip typeId — the physical tip size,
 * ignoring filter net-volume. e.g. `cybio-tip-250-ul-filter-net-vol-150-ul` → 250,
 * `tipbox-50` → 50, `tiprack-1000` → 1000.
 */
export function tipNominalVolume(typeId: string): number | undefined {
  const lc = typeId.toLowerCase();
  const ulMatch = lc.match(/(\d+)\s*-?\s*ul/);
  if (ulMatch) return parseInt(ulMatch[1], 10);
  const numMatch = lc.match(/-(\d+)(?:-|$)/);
  if (numMatch) return parseInt(numMatch[1], 10);
  return undefined;
}

/**
 * True if `returnTypeId` is an empty tip-RETURN rack that physically accepts tips
 * of `tipTypeId`: a rack carrying `supportedTipVolumes` (deviceRole "return")
 * whose volumes include the tip's nominal volume. 50/250 µL tips → CyBio tip box;
 * 1 mL tips → tip rack / 1 mL box (which accept all volumes).
 */
export function isReturnVesselForTip(
  returnTypeId: string,
  tipTypeId: string,
  config: LabwareConfig,
): boolean {
  const vols = findLabware(returnTypeId, config)?.supportedTipVolumes;
  if (!vols || vols.length === 0) return false;
  const tipVol = tipNominalVolume(tipTypeId);
  return tipVol != null && vols.includes(tipVol);
}

// ---------------------------------------------------------------------------
// Device checks
// ---------------------------------------------------------------------------

/** True if labware is a liquid waste container. */
export function isWasteDevice(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (lw.deviceRole === "waste") return true;
  // Fallback: device category with "waste" in label (but not "waste box" which is trash)
  if (lw.category === "device") {
    const label = lw.label?.toLowerCase() ?? "";
    return label.includes("waste") && !label.includes("waste box");
  }
  return false;
}

/**
 * True if labware is an empty tip-RETURN rack (deviceRole "return") — an empty
 * box/rack used tips are placed back into, rendered as a grid of empty holes.
 */
export function isReturnRack(
  typeId: string,
  config: LabwareConfig,
): boolean {
  return findLabware(typeId, config)?.deviceRole === "return";
}

/** True if labware is a tip trash device. */
export function isTrashDevice(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (lw.deviceRole === "trash") return true;
  // Fallback: device category with "trash" or "waste box" in label
  if (lw.category === "device") {
    const label = lw.label?.toLowerCase() ?? "";
    return label.includes("trash") || label.includes("waste box");
  }
  return false;
}

// ---------------------------------------------------------------------------
// Special labware checks (spec-based or label-based)
// ---------------------------------------------------------------------------

/** True if labware is a magnet device. */
export function isMagnetLabware(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (String(getSpecValue(lw, "wellShape") ?? "").toLowerCase() === "magnet")
    return true;
  return !!lw.label?.toLowerCase().includes("magnet");
}

/** True if labware is a BioShake device. */
export function isBioshakeLabware(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  if (String(getSpecValue(lw, "wellShape") ?? "").toLowerCase() === "bioshake")
    return true;
  return !!lw.label?.toLowerCase().includes("bioshake");
}

/** True if labware is a gripper. */
export function isGripperLabware(
  typeId: string,
  config: LabwareConfig,
): boolean {
  const lw = findLabware(typeId, config);
  if (!lw) return false;
  return !!lw.label?.toLowerCase().includes("gripper");
}

// ---------------------------------------------------------------------------
// Composite checks
// ---------------------------------------------------------------------------

/** True if labware should be excluded from "touched" status tracking. */
export function shouldExcludeFromTouchedStatus(
  typeId: string,
  config: LabwareConfig,
): boolean {
  return (
    isTrashDevice(typeId, config) ||
    isWasteDevice(typeId, config) ||
    isAdapter(typeId, config)
  );
}

/**
 * Returns height class for deck layout placement.
 * "tall" for tips, trash, and labware with stackHeight > 40mm.
 */
export function getHeightClass(
  typeId: string,
  config: LabwareConfig,
): string | undefined {
  if (isTipLabware(typeId, config) || isTrashDevice(typeId, config))
    return "tall";

  // Check physical stackHeight from config specs (e.g. deep-well plates, reservoirs)
  const lw = findLabware(typeId, config);
  if (lw) {
    const stackHeight = getSpecValue(lw, "stackHeight");
    if (typeof stackHeight === "number" && stackHeight > 40) return "tall";
  }

  return undefined;
}

/**
 * Get the max volume for tip labware from config specs.
 * Returns undefined if not found.
 */
export function getTipMaxVolume(
  typeId: string,
  config: LabwareConfig,
): number | undefined {
  const lw = findLabware(typeId, config);
  if (!lw) return undefined;
  const v = getSpecValue(lw, "maxVolume");
  return typeof v === "number" ? v : undefined;
}

// ---------------------------------------------------------------------------
// Border thickness (for deck layout rendering)
// ---------------------------------------------------------------------------

const BORDER_THIN = "3px";
const BORDER_MEDIUM = "6px";
const BORDER_THICK = "10px";

/**
 * Get the right border thickness for deck layout rendering.
 * Uses structured labware classification instead of typeId string matching.
 */
export function getRightBorderThickness(
  typeId: string,
  config: LabwareConfig,
): string {
  // Thick: tip trays, tip racks, trash, waste, bioshake
  if (
    isTipTray(typeId, config) ||
    isTipRack(typeId, config) ||
    isTrashDevice(typeId, config) ||
    isWasteDevice(typeId, config) ||
    isBioshakeLabware(typeId, config)
  ) {
    return BORDER_THICK;
  }

  // Medium: tip boxes, adapters, gripper
  if (
    isTipBox(typeId, config) ||
    isAdapter(typeId, config) ||
    isGripperLabware(typeId, config)
  ) {
    return BORDER_MEDIUM;
  }

  // Fall back to stackHeight spec
  const lw = findLabware(typeId, config);
  if (lw) {
    const stackHeight = getSpecValue(lw, "stackHeight");
    if (typeof stackHeight === "number") {
      if (stackHeight > 40) return BORDER_THICK;
      if (stackHeight >= 25) return BORDER_MEDIUM;
      return BORDER_THIN;
    }
  }

  return BORDER_MEDIUM;
}
